import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

export default class VariantDropdown extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    // Listen for variant updates from the variant-picker to stay in sync
    const section = this.closest('.shopify-section, dialog');
    if (section) {
      section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    }

    // Close dropdown when clicking outside
    document.addEventListener('click', this.#onDocumentClick, { signal });

    // Close dropdown on Escape
    document.addEventListener('keydown', this.#onDocumentKeydown, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  toggleDropdown() {
    const trigger = this.refs.trigger;
    if (!(trigger instanceof HTMLElement)) return;

    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    this.#setOpen(!isExpanded);
  }

  /**
   * @param {Event} event
   */
  selectVariant(event) {
    const option = /** @type {HTMLElement} */ (event.target).closest('.variant-dropdown__option');
    if (!option || option.getAttribute('aria-disabled') === 'true') return;

    const variantId = option.dataset.variantId;
    if (!variantId) return;

    this.#setOpen(false);
    this.#syncVariantPicker(variantId);
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleOptionKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectVariant(event);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#focusAdjacentOption(event.target, 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#focusAdjacentOption(event.target, -1);
    }
  }

  /**
   * @param {boolean} open
   */
  #setOpen(open) {
    const trigger = this.refs.trigger;
    const listbox = this.refs.listbox;

    if (!(trigger instanceof HTMLElement) || !(listbox instanceof HTMLElement)) return;

    trigger.setAttribute('aria-expanded', String(open));

    if (open) {
      listbox.classList.add('variant-dropdown__listbox--open');
      // Focus the selected option
      const selected = listbox.querySelector('.variant-dropdown__option--selected');
      if (selected instanceof HTMLElement) {
        selected.focus();
      }
    } else {
      listbox.classList.remove('variant-dropdown__listbox--open');
    }
  }

  /**
   * @param {EventTarget | null} current
   * @param {number} direction
   */
  #focusAdjacentOption(current, direction) {
    if (!(current instanceof HTMLElement)) return;
    const option = current.closest('.variant-dropdown__option');
    if (!option) return;

    const sibling = direction > 0 ? option.nextElementSibling : option.previousElementSibling;
    if (sibling instanceof HTMLElement) {
      sibling.focus();
    }
  }

  /**
   * Syncs the existing variant-picker when a variant is selected from this dropdown.
   * Finds the matching option value in the variant-picker and triggers a change event.
   * @param {string} variantId
   */
  #syncVariantPicker(variantId) {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const variantPicker = section.querySelector('variant-picker');
    if (!variantPicker) return;

    // Try radio buttons first — find an input whose data-variant-id matches
    const radioInput = variantPicker.querySelector(`input[data-variant-id="${variantId}"]`);
    if (radioInput instanceof HTMLInputElement) {
      radioInput.checked = true;
      radioInput.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // Try select dropdown options
    const selectOption = variantPicker.querySelector(`option[data-variant-id="${variantId}"]`);
    if (selectOption instanceof HTMLOptionElement) {
      const select = selectOption.closest('select');
      if (select) {
        select.value = selectOption.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /**
   * Handles variant update events from the variant-picker to keep this dropdown in sync.
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const { resource, data } = event.detail;
    if (!resource) return;

    if (data?.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const variantId = String(resource.id);
    const listbox = this.refs.listbox;
    const newDropdown = data?.html?.querySelector('variant-dropdown');
    const newListbox = newDropdown?.querySelector('[ref="listbox"]');

    if (listbox instanceof HTMLElement && newListbox) {
      // Re-render from the freshly computed server HTML so each row's disabled state
      // (which options are valid for the new selection) stays in sync, not just which is selected.
      morph(listbox, newListbox);
    } else if (listbox instanceof HTMLElement) {
      // Fallback: no fresh HTML to morph in, so just update the selected state.
      for (const option of listbox.querySelectorAll('.variant-dropdown__option')) {
        const isMatch = option.dataset.variantId === variantId;
        option.classList.toggle('variant-dropdown__option--selected', isMatch);
        option.setAttribute('aria-selected', String(isMatch));
      }
    }

    // Update the trigger display from the new HTML
    if (newDropdown) {
      const newSelectedDisplay = newDropdown.querySelector('[ref="selectedDisplay"]');
      const currentSelectedDisplay = this.querySelector('[ref="selectedDisplay"]');
      if (newSelectedDisplay && currentSelectedDisplay) {
        currentSelectedDisplay.innerHTML = newSelectedDisplay.innerHTML;
      }
    }
  };

  #onDocumentClick = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) {
      this.#setOpen(false);
    }
  };

  #onDocumentKeydown = (event) => {
    if (event.key === 'Escape') {
      this.#setOpen(false);
      const trigger = this.refs.trigger;
      if (trigger instanceof HTMLElement) {
        trigger.focus();
      }
    }
  };
}

if (!customElements.get('variant-dropdown')) {
  customElements.define('variant-dropdown', VariantDropdown);
}
